import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {
    useSearchFormContext,
    useTrackingContext
} from '../../../hooks';

import {
    undef,
    hasValidGuestsValues,
    submitForm,
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const componentClassNamespace = 'search-form';
const componentBaseClassName = `${componentClassNamespace}__field`;
const componentBaseHighlightClassName = `${componentBaseClassName}--highlight`;
const componentClassName = `${componentClassNamespace}__guests-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const searchFormContext = useSearchFormContext();

    const {
        media: {
            settings: {
                isMobile
            }
        },
        edap,
        search,
        form,
        form: {
            options: {
                autoProgress,
                autoSubmit,
                showIcons
            }
        },
        destination,
        dates,
        guests,
        guests: {
            actions: {
                setReadOnly,
                setCurrentValuesLatest,
                setLatestValues,
                setLatestValuesDefault
            },
            events: {
                onChange,
                onClear,
                onClose,
                onExit,
                onOpen
            },
            options: {
                actionLocation,
                adultsMax,
                adultsMin,
                allowHighlight,
                centered,
                childrenMax,
                childrenMin,
                disableTextInput,
                fadeOutDuration,
                id,
                inputIconName,
                name,
                placement,
                positionIconRight,
                showClearButton,
                showContinue,
                showInputIcon,
                showPetOption,
                wdioPrefix
            },
            settings: {
                disableButton,
                isOpen,
                readOnly
            },
            translations: {
                adultText,
                adultsText,
                childText,
                childrenText,
                guestText,
                guestsText,
                petsText,
                label,
                modalTitle,
                petLabel,
                petNoLabel,
                petYesLabel,
                petMessage,
                applyButtonLabel,
                clearButtonLabel,
                continueButtonLabel
            },
            latestValues,
            latestValues: {
                adultsCount,
                childrenCount,
                petIncluded
            }
        }
    } = searchFormContext;

    let {
        guests: {
            translations: {
                guestLabel,
                adultsLabel,
                childrenLabel,
            }
        }
    } = searchFormContext;

    const tracking = useTrackingContext();

    const className = classNames(componentBaseClassName, {
        [componentBaseHighlightClassName]: !isMobile && allowHighlight && !hasValidGuestsValues(latestValues)
    }, componentClassName);

    const inputIcon = (showInputIcon || showIcons && showInputIcon === undef) && (
        <SearchIcon name={inputIconName}/>
    ) || null;

    const guestsCount = adultsCount + childrenCount;

    adultsLabel = adultsLabel || `${adultsCount} ${adultsCount === 1 ? adultText : adultsText}`;
    childrenLabel = childrenLabel || `${childrenCount} ${childrenCount === 1 ? childText : childrenText}`;
    guestLabel = guestLabel || (
        guestsCount
            ? `${guestsCount} ${guestsCount === 1 ? guestText : guestsText}${petIncluded ? `, ${petsText}` : ''}`
            : ''
    );

    const formSuffixElement = petIncluded && (
        <p className="text-center text-muted">{petMessage}</p>
    ) || null;

    function handleChange(...args) {
        const [
            key,
            value
        ] = args;

        const changedValue = {
            [key]: value
        };

        setLatestValues(changedValue);

        onChange(...args);
    }

    function handleClear(...args) {
        setLatestValuesDefault();

        onClear(...args);
    }

    function handleClose(...args) {
        setCurrentValuesLatest();

        setReadOnly(false);

        if (autoProgress && autoSubmit && isMobile) {
            submitForm(tracking, edap, search, form, destination, dates, guests);
        }

        onClose(...args);
    }

    function handleExit(...args) {
        setCurrentValuesLatest();

        setReadOnly(false);

        onExit(...args);
    }

    function handleOpen(...args) {
        setReadOnly(true);

        onOpen(...args);
    }

    const result = {
        className,
        actionLocation,
        adultsCount,
        adultsLabel,
        adultsMax,
        adultsMin,
        applyButtonLabel,
        centered,
        childrenCount,
        childrenLabel,
        childrenMax,
        childrenMin,
        clearButtonLabel,
        continueButtonLabel,
        disableButton,
        disableTextInput,
        fadeOutDuration,
        formSuffixElement,
        guestLabel,
        id,
        inputIcon,
        isMobile,
        isOpen,
        label,
        modalTitle,
        name,
        onChange: handleChange,
        onClear: handleClear,
        onClose: handleClose,
        onExit: handleExit,
        onOpen: handleOpen,
        petIncluded,
        petLabel,
        petNoLabel,
        petYesLabel,
        placement,
        positionIconRight,
        readOnly,
        showClearButton,
        showContinue,
        showPetOption,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
